// Phase APP-3 · Founder Override modal — two-step safety flow.
// Step 1: justification entry (20..500 chars, debounce-disabled Continue).
// Step 2: confirmation checkbox + Confirm Override.
// ESC and backdrop click DISABLED during step 2 to prevent accidental dismiss.
import { useEffect, useState } from "react";
import { X, Loader2, Shield, AlertTriangle, ChevronLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../../services/api";

const PURPLE = "#8B5CF6";
const MIN = 20, MAX = 500;

export default function FounderOverrideModal({ open, onClose, request, onDone }) {
  const [step, setStep] = useState(1);
  const [justification, setJustification] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Reset state whenever the modal opens fresh
  useEffect(() => {
    if (open) {
      setStep(1); setJustification(""); setConfirmed(false);
      setBusy(false); setErr("");
    }
  }, [open, request?.request_id]);

  // Escape key gating.
  //   step 1, idle: ESC closes the modal — but we still need to STOP propagation
  //                 so the parent drawer's window-level ESC listener doesn't
  //                 also fire and close the whole drawer behind us.
  //   step 2  OR  busy: ESC is a no-op (no parent dismissal either).
  // Capture-phase + stopImmediatePropagation = win.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // Always swallow ESC while the modal is open so the parent drawer's
      // window-level listener cannot piggy-back on this keystroke.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (step === 1 && !busy) onClose?.();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, step, busy, onClose]);

  if (!open || !request) return null;

  const len = justification.trim().length;
  const justifValid = len >= MIN && len <= MAX;
  const canContinue = justifValid && !busy;
  const canConfirm = confirmed && !busy && justifValid;

  const entityTitle = request.approval_type === "packer_request"
    ? "Packer Request" : "Micro Hub Suggestion";
  const entityName = (request.entity_payload || {}).name
                       || (request.entity_payload || {}).full_name
                       || request.display_name
                       || request.request_id;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/approvals/${request.request_id}/founder-override-approve`,
                                  { justification: justification.trim() });
      const se = r.data?.side_effect || {};
      const newEntity = se.hub_id || se.user_id;
      toast.success(newEntity
                       ? `Override applied. Entity created at ${newEntity}.`
                       : "Override applied.");
      onDone?.(r.data?.status);
      onClose?.();
    } catch (e2) {
      const detail = e2?.response?.data?.detail || e2?.message || "Failed";
      const msg = typeof detail === "string" ? detail : (detail.message || JSON.stringify(detail));
      setErr(msg);
      setStep(1); // re-open step 1 with the server detail inline
    } finally {
      setBusy(false);
    }
  };

  // Backdrop only dismisses during step 1.
  const onBackdrop = () => { if (step === 1 && !busy) onClose?.(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-2"
         onClick={onBackdrop}
         data-testid="founder-override-modal"
         data-step={step}
         role="dialog" aria-modal="true">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full sm:max-w-md rounded-2xl shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Shield className="h-4 w-4" style={{ color: PURPLE }}/>
          <h3 className="text-base font-semibold flex-1">
            {step === 1 ? "Founder Override Approval" : "Confirm Override?"}
          </h3>
          {step === 1 && (
            <button type="button" onClick={onClose}
                    aria-label="Close" data-testid="override-modal-close"
                    className="p-1.5 rounded-full hover:bg-slate-100">
              <X className="h-4 w-4"/>
            </button>
          )}
        </div>

        {step === 1 && (
          <>
            <div className="px-4 py-3 space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 inline-flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0"/>
                <span>
                  This bypasses the normal approval flow.
                  The justification will be permanently logged.
                </span>
              </div>
              <div className="text-sm text-slate-700">
                Override for <span className="font-semibold">{entityName}</span>
                <span className="text-slate-400 text-xs"> · {entityTitle}</span>
              </div>
              {err && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700 inline-flex items-start gap-1.5"
                     data-testid="override-modal-error">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0"/> {err}
                </div>
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Justification <span className="text-rose-500">*</span>
                  <span className="ml-2 normal-case text-slate-400">(min {MIN}, max {MAX})</span>
                </label>
                <textarea value={justification}
                          onChange={(e) => setJustification(e.target.value)}
                          rows={5}
                          placeholder="Why is this override warranted? Be specific — the entry will be immutable."
                          data-testid="override-modal-justification"
                          autoFocus
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#8B5CF6] focus:outline-none resize-none"/>
                <div className={"text-[10px] mt-0.5 text-right " +
                                  (justifValid ? "text-slate-400"
                                    : "text-rose-600 font-semibold")}
                     data-testid="override-modal-counter">
                  {len} / {MAX} {len < MIN && `· ${MIN - len} more required`}
                  {len > MAX && ` · ${len - MAX} over limit`}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button type="button" onClick={onClose}
                      data-testid="override-modal-cancel"
                      className="text-sm px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button"
                      disabled={!canContinue}
                      onClick={() => setStep(2)}
                      data-testid="override-modal-continue"
                      data-can-submit={canContinue ? "true" : "false"}
                      className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: PURPLE }}>
                Continue →
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="px-4 py-3 space-y-3">
              <div className="text-sm text-slate-700">
                You are about to override the approval flow for{" "}
                <span className="font-semibold">{entityName}</span>.
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs"
                   data-testid="override-modal-confirm-preview">
                <div className="text-[10px] uppercase tracking-wider text-purple-700 mb-1">
                  Justification (will be logged)
                </div>
                <div className="text-slate-800 whitespace-pre-wrap break-words">
                  {justification.trim()}
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                <input type="checkbox"
                       checked={confirmed}
                       onChange={(e) => setConfirmed(e.target.checked)}
                       data-testid="override-modal-confirm-checkbox"
                       className="mt-0.5 h-4 w-4 accent-[#8B5CF6]"/>
                <span>
                  I confirm this override is necessary and will be audit-logged.
                </span>
              </label>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
              <button type="button"
                      disabled={busy}
                      onClick={() => setStep(1)}
                      data-testid="override-modal-back"
                      className="text-sm inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                <ChevronLeft className="h-3.5 w-3.5"/> Back
              </button>
              <button type="button"
                      disabled={!canConfirm}
                      onClick={submit}
                      data-testid="override-modal-confirm"
                      data-can-submit={canConfirm ? "true" : "false"}
                      className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                      style={{ backgroundColor: PURPLE }}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <Shield className="h-3.5 w-3.5"/>}
                Confirm Override
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
